import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import ProductTitle from '../../shopping/ProductTitle';
import withModal from '../../../common/WithModal';
import { getAppropriateGalleryAsset } from '../ProductHelpers';
import { checkout as checkoutGlobalAPI } from '../../../mock/globalAPI';
import {
  getActiveTableFinish,
  getIsProductInventoryChecked,
  getIsProductSoldOut,
  getProductCurrentPrice,
  getProductDetails,
  getProductName,
  getProductOriginalPrice,
  getProductSkuSplitProperty,
  getSelectedModifiersList,
  getSelectedQty,
  getShippingEstimates,
} from '../../../store/selectors/product.selectors';
import { toggleModifierOption } from '../../../store/actions/product.actions';
import { getAutomaticallyAppliedPromotion } from '../../../store/selectors/cart.selectors';

import ShippingMessage from '../../shopping/ShippingMessage';
import CallToAction from '../CallToAction';
import ProductPrice from '../../shopping/ProductPrice';
import LoadingContainer from '../Loading';
import ListModifier from '../../shopping/ProductModifiers/ListModifier';
import QuantitySelect from '../../shopping/QuantitySelect';
import Asset from '../Asset';

class ProductEditModal extends Component {
  constructor(props) {
    super(props);

    this.modifyCartItem = this.modifyCartItem.bind(this);
    this.renderListModifiers = this.renderListModifiers.bind(this);
    this.renderModalFooter = this.renderModalFooter.bind(this);
    this.renderImage = this.renderImage.bind(this);
    this.getProductDetailsWithFilteredModifiers = this.getProductDetailsWithFilteredModifiers.bind(this);
  }

  modifyCartItem() {
    const {
      item: { quantity: originalQuantity, optionValues: originalOptionValues },
      productDetails: { optionValues: newOptionValues },
      selectedQty: newQuantity,
      updateCartItemQuantityAction,
      updateCartItemModifiersAction,
    } = this.props;

    const areModifiersChanged = originalOptionValues.some(
      (_, idx) => originalOptionValues[idx].optionValue !== newOptionValues[idx].optionValue,
    );

    if (areModifiersChanged) {
      updateCartItemModifiersAction(
        this.props.item,
        newOptionValues,
        newQuantity,
      );
    } else if (originalQuantity !== newQuantity) {
      updateCartItemQuantityAction(this.props.item, newQuantity - originalQuantity);
    }
  }

  renderModalFooter() {
    const {
      isFetchingProduct,
      isCartDataPending,
      productDetails,
      shippingEstimates,
    } = this.props;
    const shippingEstimate = shippingEstimates?.[productDetails?.sku];

    return (
      <div className="cart-product-edit__update">
        <div className="pdp-add">
          <QuantitySelect />
          <button
            className={classNames('pdp-add-button btn btn-yellow', { disabled: false })}
            disabled={isFetchingProduct || isCartDataPending}
            onClick={this.modifyCartItem}
          >
            {checkoutGlobalAPI.cart.update}
          </button>
        </div>
        <div className="pdp-details-info">
          {shippingEstimate && (
            <ShippingMessage shippingEstimate={shippingEstimate} />
          )}
        </div>
      </div>
    );
  }

  renderImage() {
    const { automaticallyAppliedPromotion, productDetails } = this.props;
    const galleryImage = productDetails.gallery?.length ? getAppropriateGalleryAsset(
      productDetails.gallery[0],
      productDetails.sku,
      productDetails.collection,
      {},
      productDetails.optionValues,
      productDetails.productModifiers,
      productDetails.skuSplit?.tableFinish,
      productDetails.skuSplit?.furnitureType,
      productDetails?.skuParse,
    ) : null;

    return (
      <div className="pdp-images-container">
        <div className="pdp-image-position">
          <Asset maxWidth={356} asset={galleryImage} />
          {automaticallyAppliedPromotion && (
            <div className="badge-container">
              <CallToAction callToAction={{ badgeCopy: 'SALE' }} />
            </div>
          )}
        </div>
      </div>
    );
  }

  getProductDetailsWithFilteredModifiers() {
    const { productDetails } = this.props;
    const filteredModifiers = productDetails?.productModifiers.filter(
      ({ onSelectFunction,  modifierOperation }) => modifierOperation === 'UpdateVariant' && onSelectFunction,
    );

    const productDetailsWithFilteredModifiers = {
      ...productDetails,
      productModifiers: filteredModifiers,
    };
    return productDetailsWithFilteredModifiers;
  }

  renderListModifiers() {
    const { toggleModifierOptionAction } = this.props;
    const productDetailsWithFilteredModifiers = this.getProductDetailsWithFilteredModifiers();
    const { productModifiers } = productDetailsWithFilteredModifiers;
    return productModifiers.map((modifier) => (
      <ListModifier
        {...this.props}
        key={modifier.slug}
        toggleModifierOptionAction={toggleModifierOptionAction}
        modifier={modifier}
        withRedirect={false}
      />
    ));
  }

  render() {
    const {
      currentPrice,
      originalPrice,
      productDetails,
      productName,
    } = this.props;
    const productDetailsWithFilteredModifiers = this.getProductDetailsWithFilteredModifiers();

    return productDetails ? (
      <div className="pdp">
        <section className="cart-product-edit">
          <div className="cart-product-edit__wrapper">
            <ProductTitle
              productName={productName}
              productDetails={productDetailsWithFilteredModifiers}
            />
          </div>
          {this.renderImage()}
          <div className="cart-product-edit__wrapper">
            <div className="pdp-details">
              <ProductPrice
                currentPrice={currentPrice}
                originalPrice={originalPrice}
                productDetails={productDetails}
              />
              {this.renderListModifiers()}
              {this.renderModalFooter()}
            </div>
          </div>
        </section>
      </div>
    ) : (
      <LoadingContainer />
    );
  }
}

ProductEditModal.propTypes = {
  activeArms: PropTypes.string,
  activeColor: PropTypes.string,
  activeCushionColor: PropTypes.string,
  activeFurnitureType: PropTypes.string,
  activeLegStyle: PropTypes.string,
  activeLegs: PropTypes.string,
  activeSeat: PropTypes.string,
  activeTableFinish: PropTypes.string,
  activeMaterial: PropTypes.string,
  automaticallyAppliedPromotion: PropTypes.shape(),
  checkedInventory: PropTypes.bool,
  currentPrice: PropTypes.string,
  isCartDataPending: PropTypes.bool.isRequired,
  isFetchingProduct: PropTypes.bool.isRequired,
  item: PropTypes.shape().isRequired,
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  originalPrice: PropTypes.string,
  productDetails: PropTypes.shape(),
  productName: PropTypes.string,
  selectedModifiersList: PropTypes.arrayOf(),
  selectedQty: PropTypes.number.isRequired,
  shippingEstimates: PropTypes.shape(),
  soldOut: PropTypes.bool,
  updateCartItemModifiersAction: PropTypes.func.isRequired,
  updateCartItemQuantityAction: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  activeArms: getProductSkuSplitProperty(state, 'arms'),
  activeColor: getProductSkuSplitProperty(state, 'color'),
  activeCushionColor: getProductSkuSplitProperty(state, 'cushionColor'),
  activeFurnitureType: getProductSkuSplitProperty(state, 'furnitureType'),
  activeLegStyle: getProductSkuSplitProperty(state, 'legStyle'),
  activeLegs: getProductSkuSplitProperty(state, 'legs'),
  activeSeat: getProductSkuSplitProperty(state, 'size'),
  activeMaterial: getProductSkuSplitProperty(state, 'material'),
  activeTableFinish: getActiveTableFinish(state),
  automaticallyAppliedPromotion: getAutomaticallyAppliedPromotion(state),
  checkedInventory: getIsProductInventoryChecked(state),
  currentPrice: getProductCurrentPrice(state),
  originalPrice: getProductOriginalPrice(state),
  productDetails: getProductDetails(state),
  productName: getProductName(state),
  selectedModifiersList: getSelectedModifiersList(state),
  selectedQty: getSelectedQty(state),
  shippingEstimates: getShippingEstimates(state),
  soldOut: getIsProductSoldOut(state),
});

const mapDispatchToProps = {
  toggleModifierOptionAction: toggleModifierOption,
};

export default connect(mapStateToProps, mapDispatchToProps)(withModal(ProductEditModal));
